// CreateBudgetedHoursModal.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  updateFacility,
  getMyFacilities,
  clearFacilityState,
} from "../redux/slices/facilitySlice";
import Toast from "../components/Toast";

const CreateBudgetedHoursModal = ({ onClose, facilityData }) => {
  const dispatch = useDispatch();

  // Get the entire state first
  const fullState = useSelector((state) => state);
  const facilityState = fullState.facility || {};

  const {
    myFacilities = { data: [] },
    isLoading: isLoadingFacilities = false,
    isUpdating = false,
    updateError = null,
    successMessage = "",
  } = facilityState;

  const [facilityId, setFacilityId] = useState("");
  const [budgetedHours, setBudgetedHours] = useState("");
  const [budgetedHoursType, setBudgetedHoursType] = useState("Weekly");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  // Toast state
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  // Predefined options for budgeted hours type
  const budgetedHoursOptions = ["Weekly", "Monthly", "Quarterly", "Yearly"];

  const showToast = (message, type = "success") => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: "", type: "success" });
    }, 3000);
  };

  // Reset modal state when opened (when facilityData changes or modal opens)
  useEffect(() => {
    // Reset local state
    setError("");
    setIsLoading(false);

    // If facilityData is provided, pre-fill the form
    if (facilityData) {
      setFacilityId(facilityData.id || facilityData.facility_id || "");
      setBudgetedHours(facilityData.budgeted_hours || "");
      setBudgetedHoursType(facilityData.budgeted_hours_type || "Weekly");
    } else {
      // Reset form if no facilityData
      setFacilityId("");
      setBudgetedHours("");
      setBudgetedHoursType("Weekly");
    }
  }, [facilityData]); // Reset when facilityData changes (modal reopens)

  // Fetch facilities on component mount
  useEffect(() => {
    dispatch(getMyFacilities(1));
  }, [dispatch]);

  // Handle success message from Redux
  useEffect(() => {
    if (successMessage) {
      // Show success toast
      showToast(successMessage, "success");

      // Reset the form to initial state
      if (facilityData) {
        // If editing, keep the same facility but reset hours
        setBudgetedHours(facilityData.budgeted_hours || "");
        setBudgetedHoursType(facilityData.budgeted_hours_type || "Weekly");
      } else {
        // If creating new, reset everything
        setFacilityId("");
        setBudgetedHours("");
        setBudgetedHoursType("Weekly");
      }

      // Clear loading state
      setIsLoading(false);

      // Clear Redux success message
      dispatch(clearFacilityState());

      // Refresh facilities list to show updated data
      dispatch(getMyFacilities(1));
    }
  }, [successMessage, dispatch, facilityData]);

  // Handle error from Redux
  useEffect(() => {
    if (updateError) {
      showToast(updateError, "error");
      setIsLoading(false);
      dispatch(clearFacilityState());
    }
  }, [updateError, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!facilityId) {
      showToast("Please select a facility", "error");
      return;
    }

    if (!budgetedHours) {
      showToast("Budgeted hours are required", "error");
      return;
    }

    if (isNaN(budgetedHours) || Number(budgetedHours) <= 0) {
      showToast("Budgeted hours must be a positive number", "error");
      return;
    }

    if (!budgetedHoursType.trim()) {
      showToast("Budgeted hours type is required", "error");
      return;
    }

    setIsLoading(true);

    try {
      const updateData = {
        budgeted_hours: Number(budgetedHours),
        budgeted_hours_type: budgetedHoursType,
      };

      await dispatch(
        updateFacility({
          facilityId: facilityId,
          facilityData: updateData,
        }),
      ).unwrap();

      // Success will be handled by the useEffect watching successMessage
    } catch (err) {
      let errorMsg = "Something went wrong";
      if (err.errors) {
        errorMsg = Object.values(err.errors).flat().join(", ");
      } else if (err.message) {
        errorMsg = err.message;
      }
      showToast(errorMsg, "error");
      console.error("Update budgeted hours error:", err);
      setIsLoading(false);
    }
  };

  // If facility slice is missing, show a warning
  if (!fullState.facility) {
    return (
      <div className="announcementModal">
        <div style={{ padding: "20px", textAlign: "center", color: "#D32F2F" }}>
          <h4>Missing Redux Slice</h4>
          <p>Please add facilityReducer to your store</p>
        </div>
      </div>
    );
  }

  return (
    <div className="announcementModal">
      {/* Toast Component */}
      <Toast
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ show: false, message: "", type: "success" })}
      />

      <div className="row">
        <div className="col-md-12 mt-10 position-relative">
          <form onSubmit={handleSubmit}>
            <div className="modal-scrollable-content">
              {/* Facility Selection */}
              <div className="form-group mb-20">
                <label htmlFor="facilitySelect" className="fw-bold mb-2">
                  Facility *
                </label>
                <select
                  id="facilitySelect"
                  className="form-control"
                  value={facilityId}
                  onChange={(e) => setFacilityId(e.target.value)}
                  disabled={
                    isLoading ||
                    isUpdating ||
                    isLoadingFacilities ||
                    facilityData
                  }
                  required>
                  <option value="">Select Facility</option>
                  {isLoadingFacilities ? (
                    <option disabled>Loading facilities...</option>
                  ) : myFacilities &&
                    myFacilities.data &&
                    myFacilities.data.length > 0 ? (
                    myFacilities.data.map((facility) => (
                      <option
                        key={facility.id || facility.facility_id}
                        value={facility.id || facility.facility_id}>
                        {facility.facility_name || facility.name}
                        {facility.facility_address
                          ? ` - ${facility.facility_address}`
                          : ""}
                      </option>
                    ))
                  ) : (
                    <option disabled>No facilities found</option>
                  )}
                </select>
                {facilityData && (
                  <small className="text-muted d-block mt-1">
                    Facility is pre-selected based on your selection
                  </small>
                )}
              </div>

              {/* Budgeted Hours Input */}
              <div className="form-group mb-20">
                <label htmlFor="budgetedHours" className="fw-bold mb-2">
                  Budgeted Hours *
                </label>
                <input
                  type="number"
                  id="budgetedHours"
                  className="form-control"
                  placeholder="Enter budgeted hours"
                  value={budgetedHours}
                  onChange={(e) => setBudgetedHours(e.target.value)}
                  disabled={isLoading || isUpdating}
                  min="0"
                  step="0.5"
                  required
                />
                <small className="text-muted d-block mt-1">
                  Enter the number of budgeted hours (e.g., 40, 37.5, 20)
                </small>
              </div>

              {/* Budgeted Hours Type - Input with Datalist */}
              <div className="form-group mb-20">
                <label htmlFor="budgetedHoursType" className="fw-bold mb-2">
                  Budgeted Hours Type *
                </label>
                <input
                  type="text"
                  id="budgetedHoursType"
                  className="form-control"
                  list="budgetedHoursOptions"
                  placeholder="Type or select an option"
                  value={budgetedHoursType}
                  onChange={(e) => setBudgetedHoursType(e.target.value)}
                  disabled={isLoading || isUpdating}
                  required
                />
                <datalist id="budgetedHoursOptions">
                  {budgetedHoursOptions.map((option) => (
                    <option key={option} value={option} />
                  ))}
                </datalist>
                <small className="text-muted d-block mt-1">
                  Type a custom value or select from: Weekly, Monthly,
                  Quarterly, Yearly
                </small>
              </div>

              {/* Action Buttons */}
              <div className="d-flex gap-10 mt-20">
                <button
                  type="submit"
                  className="btn announceButton flex-grow-1"
                  disabled={isLoading || isUpdating || !facilityId}>
                  {isLoading || isUpdating ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"></span>
                      Updating...
                    </>
                  ) : (
                    "Update Budgeted Hours"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateBudgetedHoursModal;
